/**
 * useGameId - 游戏ID解析与管理组合式函数
 */
import { ref } from 'vue'
import type { CurrencyAsset } from '../gameService'
import { FALLBACK_LOCALE, type Locale } from '../i18n'
import { isNativeHelpGame } from '../config/nativeHelpGames'
import { ensureGameLoaded, resolveGameLocalConfig } from '../games/registry'
import {
  isPlaceholderModeAllowed,
  buildPlaceholderConfig,
  markPlaceholderResolved,
} from '../games/_placeholder'

export interface GameAssets {
  background?: string
  walletIcon?: string
  /** walletIcon 是否染系統色（用共用 RULE_BUTTONS.wallet 的遊戲）。 */
  tintWalletIcon?: boolean
  walletLabel?: string
  walletLabelEn?: string
  transitionTextColor?: string
  /**
   * 該遊戲 UI 主題色；由 App.vue 設成 `--color-theme` CSS 變數讓 modal 內元件繼承。
   * 取代之前各種 *TextColor 散在各處的做法。
   */
  themeColor?: string
  /**
   * Formula / winExample 公式色票(覆寫預設 `--color-formula: #ecc24a`)。
   * App.vue 設成 `--color-formula` CSS 變數。可寫 `'var(--color-theme)'`
   * 自動跟 themeColor 同步(yyz3258 採此模式)。
   */
  formulaColor?: string
  currencies?: CurrencyAsset[]
}

/**
 * 从 URL 解析 gameId —— **全專案唯一的 gameId 解析點**，支援四種格式：
 *   1. query：`?gameId=mjhl`
 *   2. query：`?game_code=mjhl`（2026-09-08 加；對接商用 snake_case 的那批）
 *   3. 路徑：`/play/gameId=mjhl`
 *   4. 本地遊戲：`/local-games/mjhl/index.html`
 *
 * ⚠️ 兩個 query 都有值時 **`gameId` 優先**（既有行為不變）。
 *
 * 🛑 不要在別處自己 `searchParams.get('gameId')` —— auth store 的 parseGameCode
 * 以前就是這樣只讀 query，路徑式 URL 下 ticket 登入帶了**空 gameCode**（2026-07-30 修）。
 *
 * 🛑 **一律轉小寫**（2026-08-20）：對接商 / 平台給的連結大小寫我們控制不了
 * （實際收到過 `?gameId=XLTB`）。registry 那條線本來就每個 resolver 各自 `.toLowerCase()`，
 * 但**送出去的兩個地方沒有**，會把原值原封不動外流：
 *   - `SET_CONFIG.gameCode` → Cocos（協議明訂「URL query 原值、**小寫**」，見根 CLAUDE.md）
 *   - `parseGameCode()` → ticket 登入 body 的 gameCode
 * 在這個唯一解析點轉一次，兩處自動修好，也讓程式對得上文件。
 */
export function readGameId(url: URL): string | null {
  const raw = readGameIdRaw(url)
  return raw ? raw.toLowerCase() : null
}

/**
 * 同 `readGameId`，但**保留 URL 原本的大小寫**。
 *
 * 🛑 **只給登入端點用**（目前唯一使用者：ticket 登入 body 的 `game_code`）。
 *    後端是拿對接商下發連結上的原字串去比對的，轉小寫會對不上
 *    （實際收過 `sandbox-h5.au-slot.net/?gameId=MJHL&l=en-US`）。
 *
 * 🛑 **其餘一律用 `readGameId`（小寫）** —— registry 查表（資料夾名是小寫）、
 *    `SET_CONFIG.gameCode`（協議明訂小寫）、loading 色票查表、ticket 快取 key
 *    都依賴小寫的穩定性；帶大寫網址進來時用原值會查不到。
 */
export function readGameIdPreservingCase(url: URL): string | null {
  return readGameIdRaw(url)
}

function readGameIdRaw(url: URL): string | null {
  // 1) 从 query 参数读取：?gameId=mjhl
  const queryGameId = url.searchParams.get('gameId')
  if (queryGameId) return queryGameId

  // 2) query 的 snake_case 別名：?game_code=mjhl（2026-09-08）
  //    🛑 擺在 `gameId` **之後** —— 兩者都給時維持既有行為（gameId 優先），
  //       這條純粹是多接受一種寫法，不改任何現有連結的解析結果。
  const queryGameCode = url.searchParams.get('game_code')
  if (queryGameCode) return queryGameCode

  const path = url.pathname

  // 3) /play/gameId=mjhl
  const m1 = path.match(/(?:^|\/)gameId=([^/?#]+)/)
  if (m1) return m1[1]!

  // 4) /local-games/mjhl/index.html => 取 local-games 后面那段
  const m2 = path.match(/^\/local-games\/([^/]+)\//)
  if (m2) return m2[1]!

  return null
}

export function useGameId() {
  const gameId = ref<string | null>(null)
  const gameCode = ref<string | null>(null)
  const gameUrl = ref<string | null>(null)
  const gameName = ref<string | null>(null)
  const pageError = ref<string | null>(null)
  const gameLoaded = ref(false)
  const gameAssets = ref<GameAssets>({})

  /**
   * 从当前 location 解析并验证 gameId
   * @returns 是否解析成功
   */
  function parseFromLocation(): boolean {
    if (gameId.value) return true

    const url = new URL(window.location.href)
    const normalized = readGameId(url)

    if (!normalized) {
      pageError.value = '缺少 gameId 参数，例如：?gameId=mjhl'
      return false
    }

    // 不再在本地验证 gameId，改为通过 API 验证
    gameId.value = normalized
    pageError.value = null
    gameLoaded.value = false
    return true
  }

  /**
   * 获取游戏的 gameCode、gameUrl 和 assets
   * - 已註冊遊戲（= 有 src/games/<gameId>/content.ts）：讀該款本地設定，**永不打 API**
   *   若 config 缺漏 → 直接 pageError，不 fallback 到 shared-web，避免不小心打到外部
   * - 其他遊戲：host 不再連 shared-web，未建立的 gameId 走通用入口 placeholder（local/dev/stg）
   * @param lang - 当前语言，用于后端按语言返回 gameName
   */
  async function resolveGameConfig(lang: Locale): Promise<void> {
    if (!gameId.value) return

    // 當款遊戲的 content / historyBoard / 自訂 modal 是 per-game lazy chunk（registry
    // glob 重構）——在這裡先載進 registry cache，後面所有同步 resolver 才有資料。
    // 未註冊 gameId 是 no-op，不影響 placeholder 流程。
    await ensureGameLoaded(gameId.value)

    // === Native 遊戲：嚴格走 host 本地 config，絕不打 shared-web API ===
    if (isNativeHelpGame(gameId.value)) {
      const local = resolveGameLocalConfig(gameId.value)
      if (!local) {
        pageError.value = `游戏 "${gameId.value}" 的 content 載入失敗，請確認 src/games/${gameId.value}/content.ts 的 *_CONTENT export`
        return
      }

      gameCode.value = local.gameCode || gameId.value
      gameUrl.value = local.gameUrl || null
      gameName.value =
        lang === 'en'
          ? local.gameName.en || local.gameName.zh
          : local.gameName.zh || local.gameName.en

      if (!gameUrl.value) {
        pageError.value = `游戏 "${gameId.value}" 本地 config 未配置 gameUrl`
        return
      }

      gameAssets.value = {
        background: local.assets?.background,
        walletIcon: local.assets?.walletIcon,
        tintWalletIcon: local.assets?.tintWalletIcon,
        walletLabel: local.assets?.walletLabel,
        formulaColor: local.assets?.formulaColor,
        walletLabelEn: local.assets?.walletLabelEn,
        transitionTextColor: local.assets?.transitionTextColor,
        themeColor: local.assets?.themeColor,
        currencies: local.assets?.currencies,
      }
      return
    }

    // === 非 native：host 已**完全不跟 shared-web 溝通**（移除 fetchGameInfo）。 ===
    // 未建立成 native 的 gameId 一律走「通用入口」placeholder：iframe 指到
    //   `${GAME_H5_URL}/games/<gameCode>/index.html`，paytable/rules/history 用原生空白 modal。
    // 安全閘維持 local/dev/stg 開放；sandbox/rel/prod 不打任何 API、直接報錯（未建立）。
    if (isPlaceholderModeAllowed()) {
      const ph = buildPlaceholderConfig(gameId.value)
      markPlaceholderResolved(gameId.value)
      gameCode.value = ph.gameCode
      gameUrl.value = ph.gameUrl
      gameName.value = lang === 'en' ? ph.gameName.en : ph.gameName.zh
      gameAssets.value = {
        themeColor: ph.assets?.themeColor,
        transitionTextColor: ph.assets?.transitionTextColor,
      }
      pageError.value = null
      return
    }
    pageError.value = `游戏 "${gameId.value}" 尚未建立`
    gameCode.value = null
    gameUrl.value = null
    gameName.value = null
    gameAssets.value = {}
  }

  /**
   * 解析當款遊戲設定（gameUrl / gameName / assets），失敗一律轉成 `pageError`。
   *
   * 🛑 這層 try/catch **不要拿掉**。這是開機鏈上唯一會 await 非同步資源的地方
   *    （`ensureGameLoaded` 載 per-game lazy chunk），而 App.vue 的 `initPage()`
   *    是在 `onMounted` 裡 fire-and-forget 呼叫的。少了 catch，任何 throw 都會變成
   *    unhandled rejection，`pageError` 沒被設、`gameLoaded` 也永遠不轉 true →
   *    玩家卡在無限 loading 畫面，沒有錯誤訊息也沒有重試。
   *    （registry 那層已經先吞掉 chunk 載入失敗，這裡是第二道兜底。）
   */
  async function fetchGameCode(lang: Locale = FALLBACK_LOCALE): Promise<void> {
    if (!gameId.value) return
    try {
      await resolveGameConfig(lang)
    } catch (e) {
      console.error('[web][useGameId] fetchGameCode 失敗:', e)
      pageError.value = `游戏 "${gameId.value}" 初始化失敗，請重新整理頁面`
      gameUrl.value = null
    }
  }

  /**
   * 重置状态
   */
  function reset() {
    pageError.value = null
    gameLoaded.value = false
    gameCode.value = null
    gameUrl.value = null
    gameName.value = null
    gameAssets.value = {}
  }

  /**
   * 标记游戏已加载
   */
  function markLoaded() {
    gameLoaded.value = true
  }

  return {
    gameId,
    gameCode,
    gameUrl,
    gameName,
    gameAssets,
    pageError,
    gameLoaded,
    parseFromLocation,
    fetchGameCode,
    markLoaded,
    reset,
  }
}
